import React, { useState, useEffect } from 'react';
import { Modal, Form, Spinner } from 'react-bootstrap';
import { Building2, Calendar, X, Save, AlertTriangle, CheckCircle, Users, Briefcase, Hash, Plus, UserCheck } from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import moment from 'moment';
import { toast } from 'sonner';

import {
  TrendingUp
} from 'lucide-react';
import './employee-form.css';
import BrandSelect, { optionsFromChildren } from '../../Common/BrandSelect';

// InputField component defined outside to prevent recreating on each render
const InputField = ({ icon: Icon, label, name, type = 'text', required = false, value, onChange, showValidation, errors, ...props }) => (
  <div className="input-group">
    <label className="input-label">
      {label}
      {required && <span className="required-indicator">*</span>}
    </label>
    <div className="input-container">
      <div className="input-icon">
        <Icon size={16} />
      </div>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className={`modern-input ${showValidation && errors?.[name] ? 'error' : ''}`}
        {...props}
      />
    </div>
    {showValidation && errors?.[name] && (
      <div className="error-message">
        <AlertTriangle size={12} />
        <span>{errors[name]}</span>
      </div>
    )}
  </div>
);

const UpdateAssignmentModal = ({ show, handleClose, employeeId, companyId, currentAssignment, mode = 'create', onSuccess }) => {
  const isEditMode = mode === 'edit';

  const [formData, setFormData] = useState({
    department_id: '',
    position_id: '',
    manager_employee_id: '',
    valid_from: moment().format('YYYY-MM-DD'),
    cost_center: '',
    grade: ''
  });
  const [originalValidFrom, setOriginalValidFrom] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const [errors, setErrors] = useState({});

  // Nested modal states
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // New department form data
  const [newDepartment, setNewDepartment] = useState({ name: '', description: '', cost_center: '' });
  const [departmentErrors, setDepartmentErrors] = useState({});

  // New position form data
  const [newPosition, setNewPosition] = useState({ title: '', description: '', level: '' });
  const [positionErrors, setPositionErrors] = useState({});

  // Update form data when modal is shown
  useEffect(() => {
    if (show) {
      if (isEditMode && currentAssignment) {
        // Pre-fill form with existing data when editing
        setFormData({
          department_id: currentAssignment.department_id || '',
          position_id: currentAssignment.position_id || '',
          manager_employee_id: currentAssignment.manager_employee_id || '',
          valid_from: currentAssignment.valid_from || moment().format('YYYY-MM-DD'),
          cost_center: currentAssignment.cost_center || '',
          grade: currentAssignment.grade || ''
        });
        setOriginalValidFrom(currentAssignment.valid_from || null);
      } else {
        // Reset form for create mode. New assignments inherit the current
        // manager so the dropdown isn't blank when the admin is just
        // changing dept/position — they can clear it explicitly if desired.
        setFormData({
          department_id: '',
          position_id: '',
          manager_employee_id: currentAssignment?.manager_employee_id || '',
          valid_from: moment().format('YYYY-MM-DD'),
          cost_center: '',
          grade: ''
        });
        setOriginalValidFrom(null);
      }
      // Reset validation states when modal opens
      setShowValidation(false);
      setErrors({});
      setError(null);
      loadReferenceData();
    }
  }, [show]);

  const loadReferenceData = async () => {
    setLoadingData(true);
    try {
      const [deptResponse, posResponse, empResponse] = await Promise.all([
        axios.get(getApiUrl(`${companyId}/hr/departments`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/hr/positions`), { withCredentials: true }),
        // pageSize=500: GET /hr/employees paginates (default 10). For a
        // manager picker we want everyone in one shot — no realistic
        // single-tenant company exceeds 500, and the endpoint already
        // splits active vs inactive for us so we just take active.
        axios.get(getApiUrl(`${companyId}/hr/employees?page=0&pageSize=500`), { withCredentials: true })
      ]);

      setDepartments(deptResponse.data?.data || []);
      setPositions(posResponse.data?.data || []);
      setEmployees(empResponse.data?.data?.activeEmployees || []);
    } catch (err) {
      console.error('Error loading reference data:', err);
      setError('Det gick inte att ladda referensdata');
    } finally {
      setLoadingData(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.department_id && !formData.position_id) {
      newErrors.general = 'Minst en av position eller avdelning måste väljas';
    }

    if (!formData.valid_from) {
      newErrors.valid_from = 'Datum måste anges';
    } else if (!isEditMode) {
      // Only validate past dates in create mode
      // In edit mode, date is readonly so no validation needed
      const selectedDate = new Date(formData.valid_from);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        newErrors.valid_from = 'Datum kan inte vara i det förflutna';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name] || errors.general) {
      setErrors(prev => ({ ...prev, [name]: null, general: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowValidation(true);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiUrl = getApiUrl(`${companyId}/hr/employees/${employeeId}/assignment`);

      // A manager change is a temporal HR event ("from this date, X is
      // employee Y's manager"), not a typo correction — so we route it
      // through POST (close current + open new row) regardless of whether
      // the modal is in create or edit mode. Corrections to dept /
      // position / cost_center / grade in edit mode still go via PUT
      // (in-place update), which is the existing behavior.
      const managerChanged = isEditMode && (
        (formData.manager_employee_id || null) !== (currentAssignment?.manager_employee_id || null)
      );
      const useCreate = !isEditMode || managerChanged;

      const payload = {
        // For new-assignment writes, anchor the temporal change to today
        // rather than reusing the existing row's valid_from (which would
        // be a no-op or worse). The backend will close the current
        // assignment as of yesterday and open this one from today.
        valid_from: useCreate && isEditMode ? moment().format('YYYY-MM-DD') : formData.valid_from,
      };

      if (formData.department_id) payload.department_id = formData.department_id;
      if (formData.position_id) payload.position_id = formData.position_id;
      if (formData.cost_center) payload.cost_center = formData.cost_center;
      if (formData.grade) payload.grade = formData.grade;
      // Always send manager_employee_id (including null) so "remove
      // manager" round-trips correctly. The backend treats undefined as
      // "leave as-is" and null as "clear it".
      payload.manager_employee_id = formData.manager_employee_id || null;

      if (useCreate) {
        // Create new assignment — either pure create mode, or edit mode
        // where the manager changed. Either way, this preserves the
        // temporal history of the assignment chain.
        await axios.post(apiUrl, payload, { withCredentials: true });
      } else {
        // Edit mode, only non-manager fields changed → in-place correction.
        await axios.put(apiUrl, payload, { withCredentials: true });
      }

      if (onSuccess) onSuccess();
      resetAndClose();
    } catch (err) {
      console.error('Error updating assignment:', err);
      setError(err.response?.data?.message || 'Det gick inte att uppdatera placeringen');
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setFormData({
      department_id: currentAssignment?.department_id || '',
      position_id: currentAssignment?.position_id || '',
      manager_employee_id: currentAssignment?.manager_employee_id || '',
      valid_from: isEditMode ? (currentAssignment?.valid_from || moment().format('YYYY-MM-DD')) : moment().format('YYYY-MM-DD'),
      cost_center: currentAssignment?.cost_center || '',
      grade: currentAssignment?.grade || ''
    });
    setError(null);
    setShowValidation(false);
    setErrors({});
    handleClose();
  };

  // Department creation handlers
  const handleCreateDepartment = async () => {
    const newErrors = {};
    if (!newDepartment.name.trim()) {
      newErrors.name = 'Avdelningsnamn är obligatoriskt';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setDepartmentErrors(newErrors);
      return;
    }

    setIsCreating(true);
    try {
      const response = await axios.post(
        getApiUrl(`${companyId}/hr/departments`),
        newDepartment,
        { withCredentials: true }
      );

      if (response.data.status === 'success') {
        await loadReferenceData(); // Reload departments
        const createdDeptId = response.data.departmentId; // Backend returns departmentId directly
        
        // Auto-select the newly created department
        if (createdDeptId) {
          setFormData(prev => ({ ...prev, department_id: createdDeptId }));
        }
        
        setShowDepartmentModal(false);
        setNewDepartment({ name: '', description: '', cost_center: '' });
        setDepartmentErrors({});

        toast.success('Avdelningen har skapats framgångsrikt');
      }
    } catch (err) {
      console.error('Error creating department:', err);
      toast.error(err.response?.data?.message || 'Ett fel inträffade vid skapande av avdelningen');
    } finally {
      setIsCreating(false);
    }
  };

  // Position creation handlers
  const handleCreatePosition = async () => {
    const newErrors = {};
    if (!newPosition.title.trim()) {
      newErrors.title = 'Befattning är obligatoriskt';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setPositionErrors(newErrors);
      return;
    }

    setIsCreating(true);
    try {
      const response = await axios.post(
        getApiUrl(`${companyId}/hr/positions`),
        newPosition,
        { withCredentials: true }
      );

      if (response.data.status === 'success') {
        await loadReferenceData(); // Reload positions
        const createdPosId = response.data.positionId; // Backend returns positionId directly
        
        // Auto-select the newly created position
        if (createdPosId) {
          setFormData(prev => ({ ...prev, position_id: createdPosId }));
        }
        
        setShowPositionModal(false);
        setNewPosition({ title: '', description: '', level: '' });
        setPositionErrors({});

        toast.success('Befattningen har skapats framgångsrikt');
      }
    } catch (err) {
      console.error('Error creating position:', err);
      toast.error(err.response?.data?.message || 'Ett fel inträffade vid skapande av befattningen');
    } finally {
      setIsCreating(false);
    }
  };

  const SelectField = ({ icon: Icon, label, name, required = false, children, value, onChange, showValidation, errors, ...props }) => (
    <div className="input-group">
      <label className="input-label">
        {label}
        {required && <span className="required-indicator">*</span>}
      </label>
      <div className="input-container">
        <div className="input-icon">
          <Icon size={16} />
        </div>
        <BrandSelect
          name={name}
          value={value}
          onChange={onChange}
          options={optionsFromChildren(children).options}
          placeholder={optionsFromChildren(children).placeholder}
          isInvalid={Boolean(showValidation && errors[name])}
          isDisabled={Boolean(props.disabled)}
        />
      </div>
      {showValidation && errors[name] && (
        <div className="error-message">
          <AlertTriangle size={12} />
          <span>{errors[name]}</span>
        </div>
      )}
    </div>
  );

  return (
    <>

      <Modal className="emp-modal" show={show} onHide={resetAndClose} size="lg" backdrop="static" centered>
        <div className="assignment-modal-content">
          {/* Header */}
          <div className="assignment-modal-header">
            <button className="close-button" onClick={resetAndClose} aria-label="Stäng modal">
              <X size={16} />
            </button>
            <div className="d-flex align-items-center gap-3">
              <Building2 size={24} color="#2563eb" />
              <div>
                <h3 className="mb-1" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                  {isEditMode ? 'Redigera placering' : 'Skapa ny placering'}
                </h3>
                <p className="mb-0" style={{ color: '#64748b', fontSize: '0.875rem' }}>
                  {isEditMode ? 'Uppdatera position och avdelning' : 'Lägg till position och avdelning med giltighetsstart'}
                </p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="assignment-modal-body">
            {loadingData ? (
              <div className="loading-container">
                <Spinner animation="border" variant="primary" />
                <div className="loading-text">Laddar avdelningar och positioner...</div>
              </div>
            ) : (
              <Form onSubmit={handleSubmit}>
                {/* Error message */}
                {(error || errors.general) && (
                  <div className="validation-summary">
                    <AlertTriangle size={16} className="validation-icon" />
                    <div className="validation-content">
                      <div className="validation-title">Ett fel uppstod</div>
                      <p style={{ fontSize: '0.875rem', color: '#dc2626', margin: 0 }}>
                        {error || errors.general}
                      </p>
                    </div>
                  </div>
                )}

                {/* Current value */}
                {currentAssignment && (
                  <div className="current-value-card">
                    <Briefcase size={20} className="current-value-icon" />
                    <div className="current-value-content">
                      <div className="current-value-label">Nuvarande placering</div>
                      <div className="current-value-text">
                        {currentAssignment.position || 'Ingen position'}
                        {currentAssignment.department && ` • ${currentAssignment.department}`}
                      </div>
                      {(currentAssignment.manager_firstname || currentAssignment.manager_lastname) && (
                        <div style={{ fontSize: '0.8125rem', color: '#1e3a8a', marginTop: '0.25rem' }}>
                          Chef: {[currentAssignment.manager_firstname, currentAssignment.manager_lastname].filter(Boolean).join(' ')}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Info message */}
                <div className="form-info">
                  <CheckCircle size={16} className="info-icon" />
                  <div className="info-text">
                    <strong>OBS:</strong> Minst en av position eller avdelning måste väljas för att spara placeringen.
                  </div>
                </div>

                {/* Position and Department */}
                <div className="form-section">
                  <div className="section-title">
                    <Briefcase size={18} />
                    Position och avdelning
                  </div>

                  <div className="form-grid">
                    <div>
                      <SelectField
                        icon={Briefcase}
                        label="Position"
                        name="position_id"
                        value={formData.position_id}
                        onChange={handleChange}
                      >
                        <option value="">-- Välj position --</option>
                        {positions.map(pos => (
                          <option key={pos.position_id} value={pos.position_id}>
                            {pos.title} {pos.level ? `(${pos.level})` : ''}
                          </option>
                        ))}
                      </SelectField>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary mt-2 d-flex align-items-center"
                        onClick={() => setShowPositionModal(true)}
                      >
                        <Plus size={14} className="me-1" />
                        Skapa ny position
                      </button>
                    </div>

                    <div>
                      <SelectField
                        icon={Users}
                        label="Avdelning"
                        name="department_id"
                        value={formData.department_id}
                        onChange={handleChange}
                      >
                        <option value="">-- Välj avdelning --</option>
                        {departments.map(dept => (
                          <option key={dept.department_id} value={dept.department_id}>
                            {dept.name}
                          </option>
                        ))}
                      </SelectField>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary mt-2 d-flex align-items-center"
                        onClick={() => setShowDepartmentModal(true)}
                      >
                        <Plus size={14} className="me-1" />
                        Skapa ny avdelning
                      </button>
                    </div>
                  </div>
                </div>

                {/* Reporting structure */}
                <div className="form-section">
                  <div className="section-title">
                    <UserCheck size={18} />
                    Chef
                  </div>

                  <div className="form-grid">
                    <SelectField
                      icon={UserCheck}
                      label="Närmaste chef"
                      name="manager_employee_id"
                      value={formData.manager_employee_id}
                      onChange={handleChange}
                      showValidation={showValidation}
                      errors={errors}
                    >
                      <option value="">— Ingen chef —</option>
                      {employees
                        .filter(emp => emp.employee_id !== employeeId)
                        .map(emp => {
                          const name = emp.firstname && emp.lastname
                            ? `${emp.firstname} ${emp.lastname}`
                            : emp.name || emp.email || emp.employee_id;
                          return (
                            <option key={emp.employee_id} value={emp.employee_id}>
                              {name}
                            </option>
                          );
                        })}
                    </SelectField>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.5rem', marginBottom: 0 }}>
                    Chefen godkänner den anställdes tidrapporter och utlägg. Lämna tomt om den anställde själv ska godkänna.
                  </p>
                </div>

                {/* Additional Details */}
                <div className="form-section">
                  <div className="section-title">
                    <Hash size={18} />
                    Ytterligare uppgifter
                  </div>

                  <div className="form-grid">
                    <InputField
                      icon={Hash}
                      label="Kostnadsställe"
                      name="cost_center"
                      type="text"
                      placeholder="ENG-001"
                      value={formData.cost_center}
                      onChange={handleChange}
                    />

                    <InputField
                      icon={TrendingUp}
                      label="Grad"
                      name="grade"
                      type="text"
                      placeholder="Senior"
                      value={formData.grade}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Validity Period */}
                <div className="form-section">
                  <div className="section-title">
                    <Calendar size={18} />
                    Giltighetstid
                  </div>

                  <div className="form-grid">
                    <InputField
                      icon={Calendar}
                      label="Gäller från"
                      name="valid_from"
                      type="date"
                      required={true}
                      value={formData.valid_from}
                      onChange={handleChange}
                      showValidation={showValidation}
                      errors={errors}
                      disabled={isEditMode}
                      min={isEditMode ? undefined : moment().format('YYYY-MM-DD')}
                    />
                  </div>
                </div>
              </Form>
            )}
          </div>

          {/* Actions */}
          <div className="modal-actions">
            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
              * Obligatoriska fält
            </div>
            <div className="d-flex gap-3">
              <button
                className="action-button btn-cancel"
                onClick={resetAndClose}
                disabled={loading || loadingData}
              >
                Avbryt
              </button>
              <button
                className="action-button btn-save"
                onClick={handleSubmit}
                disabled={loading || loadingData}
              >
                {loading ? (
                  <>
                    <div className="loading-spinner"></div>
                    Sparar...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Spara placering
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Department Creation Modal */}
      <Modal className="emp-modal" 
        show={showDepartmentModal} 
        onHide={() => {
          setShowDepartmentModal(false);
          setNewDepartment({ name: '', description: '', cost_center: '' });
          setDepartmentErrors({});
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Users size={20} className="me-2" />
            Skapa ny avdelning
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                Avdelningsnamn <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="T.ex. Engineering, Sales, Marketing"
                value={newDepartment.name}
                onChange={(e) => {
                  setNewDepartment(prev => ({ ...prev, name: e.target.value }));
                  setDepartmentErrors(prev => ({ ...prev, name: null }));
                }}
                isInvalid={!!departmentErrors.name}
              />
              <Form.Control.Feedback type="invalid">
                {departmentErrors.name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Beskrivning</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Kort beskrivning av avdelningen"
                value={newDepartment.description}
                onChange={(e) => setNewDepartment(prev => ({ ...prev, description: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Kostnadsställe</Form.Label>
              <Form.Control
                type="text"
                placeholder="T.ex. 1000, ENG-001"
                value={newDepartment.cost_center}
                onChange={(e) => setNewDepartment(prev => ({ ...prev, cost_center: e.target.value }))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setShowDepartmentModal(false);
              setNewDepartment({ name: '', description: '', cost_center: '' });
              setDepartmentErrors({});
            }}
            disabled={isCreating}
          >
            Avbryt
          </button>
          <button
            className="btn btn-primary"
            onClick={handleCreateDepartment}
            disabled={isCreating}
          >
            {isCreating ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Skapar...
              </>
            ) : (
              <>
                <Plus size={16} className="me-1" />
                Skapa avdelning
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Position Creation Modal */}
      <Modal className="emp-modal" 
        show={showPositionModal} 
        onHide={() => {
          setShowPositionModal(false);
          setNewPosition({ title: '', description: '', level: '' });
          setPositionErrors({});
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Briefcase size={20} className="me-2" />
            Skapa ny position
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                Befattning <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="T.ex. Software Engineer, Sales Manager"
                value={newPosition.title}
                onChange={(e) => {
                  setNewPosition(prev => ({ ...prev, title: e.target.value }));
                  setPositionErrors(prev => ({ ...prev, title: null }));
                }}
                isInvalid={!!positionErrors.title}
              />
              <Form.Control.Feedback type="invalid">
                {positionErrors.title}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Beskrivning</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Kort beskrivning av positionen"
                value={newPosition.description}
                onChange={(e) => setNewPosition(prev => ({ ...prev, description: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nivå</Form.Label>
              <Form.Control
                type="text"
                placeholder="T.ex. Junior, Senior, Lead"
                value={newPosition.level}
                onChange={(e) => setNewPosition(prev => ({ ...prev, level: e.target.value }))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setShowPositionModal(false);
              setNewPosition({ title: '', description: '', level: '' });
              setPositionErrors({});
            }}
            disabled={isCreating}
          >
            Avbryt
          </button>
          <button
            className="btn btn-primary"
            onClick={handleCreatePosition}
            disabled={isCreating}
          >
            {isCreating ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Skapar...
              </>
            ) : (
              <>
                <Plus size={16} className="me-1" />
                Skapa position
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UpdateAssignmentModal;
